import { type KeyboardEvent, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Avatar,
  Dropdown,
  DropdownContent,
  DropdownTextInput,
  Icon,
  Tabs,
  Typography,
} from 'shared/ui'
import { TextFieldPlaceholder, makeTextFieldAutoFocus } from 'shared/ui/TextField'
import { useElementWidth } from 'shared/hooks'
import { useAddMembersContext } from 'pages/settings/pages/members/pages/addMembers/context/addMembersContext'
import styles from './styles.module.scss'

export const MemberEmailField = observer(() => {
  const {
    inputValue,
    isDropdownOpen,
    integrationTabs,
    activeTabKey,
    setActiveTab,
    filteredCrmUsers,
    addEmailFromCrmUser,
    addEmailFromDropdown,
    hasIntegrations,
    handleEmailInput,
    addEmailFromInput,
    focusTrigger,
    isEmailInputDisabled,
    toggleDropdown,
    emailError,
  } = useAddMembersContext()

  const ref = useRef<HTMLLabelElement | null>(null)
  const triggerWidth = useElementWidth(ref) ?? 0

  const getPlaceholderText = () => {
    if (inputValue) {
      return inputValue
    }
    return 'Enter or choose a member'
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      addEmailFromInput()
    }
  }

  const renderDropdownContent = () => {
    return (
      <>
        {hasIntegrations && (
          <Tabs
            tabs={integrationTabs}
            activeTabKey={activeTabKey}
            handleSelectTab={(tab) => setActiveTab(tab.key)}
            withStopPropagation
            tabsClassName={styles.tabs}
            tabClassName={styles.tab}
          />
        )}

        <div className={styles.dropdownOptions}>
          {filteredCrmUsers.length === 0 && !inputValue && (
            <div className={styles.dropdownEmpty}>
              <Typography variant='body-md-regular' color='tertiary' ariaLabel='MembersEmptyState'>
                No members found
              </Typography>
            </div>
          )}

          {filteredCrmUsers.map((user) => (
            <div
              key={`${user.integrationId}-${user.id}`}
              className={styles.userItem}
              onClick={() => addEmailFromCrmUser(user)}
            >
              <div className={styles.dropdownLabel}>
                <Avatar className={styles.dropdownAvatar} info={user.avatarInfo} size={16} />
                <Typography
                  className={styles.dropdownName}
                  variant='text-input-field-r'
                  tooltipProps={{ label: user.name }}
                  ariaLabel='MemberName'
                  ellipsis
                >
                  {user.name}
                </Typography>
              </div>
              <Typography
                className={styles.dropdownEmail}
                variant='body-xs-regular'
                tooltipProps={{ label: user.email }}
                ariaLabel='MemberEmail'
                color='tertiary'
                ellipsis
              >
                {user.email}
              </Typography>
            </div>
          ))}

          {!!inputValue && !filteredCrmUsers.length && (
            <div className={styles.addEmail} onClick={addEmailFromDropdown}>
              <div className={styles.addEmailIcon}>
                <Icon icon='add' fontSize={16} tertiary />
              </div>
              <Typography variant='body-md-regular' ariaLabel='AddMemberEmail'>
                Add {inputValue}
              </Typography>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <Dropdown
      ariaLabel='IntegrationsMembersSearch'
      show={isDropdownOpen && !isEmailInputDisabled}
      onToggle={toggleDropdown}
      disabled={isEmailInputDisabled}
      placement='bottom-start'
      triggerComponent={() => (
        <TextFieldPlaceholder
          ref={ref}
          variant='stroke'
          size='medium'
          className={styles.textFieldPlaceholder}
          disabled={isEmailInputDisabled}
          error={emailError}
          placeholder={
            <Typography
              variant='text-input-field-r'
              color={
                inputValue ? 'var(--content-primary-primary)' : 'var(--content-neutral-primary)'
              }
              ariaLabel='IntegrationsMembersSearch_Placeholder'
              ellipsis
              tag='div'
            >
              {getPlaceholderText()}
            </Typography>
          }
        />
      )}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [-4, -40],
            },
          },
        ],
      }}
      customComponent={() => (
        <DropdownContent noPadding>
          <DropdownTextInput
            dropdownTextInputClassName={emailError ? styles.dropdownTextInput : undefined}
            textFieldProps={{
              value: inputValue,
              onChange: handleEmailInput,
              disabled: isEmailInputDisabled,
              error: emailError,
              InputProps: {
                placeholder: 'Enter or choose a member',
                onKeyDown: handleKeyDown,
              },
              mainActions: [makeTextFieldAutoFocus({ focusTrigger })],
            }}
          />
          {renderDropdownContent()}
        </DropdownContent>
      )}
      width={triggerWidth + 8}
      margin={0}
    />
  )
})
